interface HasName {
  name: string;
}

// Keep a record of all the names we have lowercased
const lowerCaseNameMap: Record<string, string> = {};
export function sortByName<T extends HasName>(a: T, b: T): number {
  if (!lowerCaseNameMap[a.name]) {
    lowerCaseNameMap[a.name] = a.name.toLocaleLowerCase();
  }
  if (!lowerCaseNameMap[b.name]) {
    lowerCaseNameMap[b.name] = b.name.toLocaleLowerCase();
  }
  return lowerCaseNameMap[a.name].localeCompare(lowerCaseNameMap[b.name]);
}

export function sortByAlpha<T>(keys: Array<keyof T>) {
  if (keys.length === 0) {
    return () => 0;
  }
  let nextKeySorter: (a: T, b: T) => number;
  const key = keys[0];
  if (keys.length > 1) {
    nextKeySorter = sortByAlpha(keys.slice(1));
  }
  return (a: T, b: T): number => {
    const aValue = getLowerCaseMapValue(a[key]);
    const bValue = getLowerCaseMapValue(b[key]);
    if (aValue && bValue) {
      const result = aValue.localeCompare(bValue);
      if (result === 0 && nextKeySorter) {
        return nextKeySorter(a, b);
      }
      return result;
    }
    return aValue ? -1 : bValue ? 1 : nextKeySorter ? nextKeySorter(a, b) : 0;
  };
}

function getLowerCaseMapValue(value: any): string {
  if (!value) {
    return null;
  }
  const valueString = typeof value === 'string' ? value : value.toString();
  if (!lowerCaseNameMap[valueString]) {
    lowerCaseNameMap[valueString] = valueString.toLocaleLowerCase();
  }
  return lowerCaseNameMap[valueString];
}

export function sortBy<T = any>(...properties: Array<string>): (a: T, b: T) => number {
  return (obj1: any, obj2: any): number => {
    const props = properties.filter((prop) => typeof prop === 'string') as string[];
    let i = 0;
    let result = 0;

    const numberOfProperties = props.length;

    while (result === 0 && i < numberOfProperties) {
      result = sort(props[i])(obj1, obj2);
      i++;
    }

    return result;
  };
}

function sort(property: string): any {
  let sortOrder = 1;

  if (property[0] === '-') {
    sortOrder = -1;
    property = property.substr(1);
  }

  const transformer = (index: string, value: any) => {
    if (Array.isArray(value)) {
      // This will sort the array by the length then alpha by first entry if a string
      const response = value.length;
      if (value.length && typeof value[0] === 'string') {
        return `${response}${value[0].toLocaleLowerCase()}`;
      }

      return value.length;
    }
    return typeof value === 'string' ? value.toLocaleLowerCase() : value;
  };

  return (a: any, b: any): number => {
    let result = 0;

    const mappedA = transformer(property, objectPath(a, property));
    const mappedB = transformer(property, objectPath(b, property));

    if (isNullOrUndefined(mappedA) && mappedB) {
      result = 1 * sortOrder;
    } else if (mappedA && isNullOrUndefined(mappedB)) {
      result = -1 * sortOrder;
    } else if (mappedA < mappedB) {
      result = -1;
    } else if (mappedA > mappedB) {
      result = 1;
    }

    return result * sortOrder;
  };
}

function objectPath(object: any, path: string): any {
  const pathParts = path.split('.');

  let result: any = object;

  try {
    pathParts.forEach((part) => {
      result = result[part];
    });
  } catch (e) {
    return null;
  }

  return result;
}

function isNullOrUndefined(object: any): boolean {
  return object === null || object === undefined;
}
